import React from "react";
import { Box, Button, Divider, Typography, Input, IconButton, Tooltip } from "@mui/joy";
import { Activity, Assignment, DynamicProperty } from "../../../api/assignments/Assignments.domain";
import {
  AssignmentStatus,
  ActivityTypeCode,
  ActivityReturnStatus,
  ResponseDetailsPropertiesKeyCode,
  FailureReasonCode,
  MissionStatus,
} from "../../../api/assignments/Assignments.codes";
import { Rule } from "../../../api/rules/Rules.domain";
import CommonReadOnlyLayout from "./CommonReadOnlyLayout";
import ActivityEditor from "./ActivityEditor";
import MissionStatusSelector from "./MissionStatusSelector";
import { useAlert } from "../../../hooks/useAlert";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { useRules } from "../../../contexts/controllers/useRules";
import { useAssignments } from "../../../contexts/controllers/useAssignments";
import { useConfirmation } from "../../../contexts/ConfirmationContext";
import { validateActivitiesList } from "../../../utils/activityValidation";
import { isNextActivityExternal, getNextActivityRule } from "../../../utils/activitySequence";
import { getSuggestedValuesByIndex } from "../../../utils/activitySuggestedValues";

interface AssignmentEditorFieldsProps {
  assignment: Assignment;
  onChange: (updated: Assignment) => void;
  activitiesChanged: boolean;
}

const AssignmentEditorFields: React.FC<AssignmentEditorFieldsProps> = ({
  assignment,
  onChange,
  activitiesChanged,
}) => {
  const { rules } = useRules();
  const { updateAdjustoId, updateMissionStatus } = useAssignments();
  const { confirm } = useConfirmation();
  const [localActivities, setLocalActivities] = React.useState<Activity[]>(
    assignment.activities || [],
  );
  const [localStatus, setLocalStatus] = React.useState<AssignmentStatus>(
    assignment.assignmentStatus,
  );
  const [missionStatus, setMissionStatus] = React.useState<MissionStatus>(assignment.missionStatus);
  const [adjustoId, setAdjustoId] = React.useState<string>(assignment.adjustoId || "");
  const [savingAdjustoId, setSavingAdjustoId] = React.useState<boolean>(false);
  const [savingMissionStatus, setSavingMissionStatus] = React.useState<boolean>(false);
  const { showAlert } = useAlert();

  // Keep local state in sync with props
  React.useEffect(() => {
    setLocalActivities(assignment.activities || []);
    setLocalStatus(assignment.assignmentStatus);
    setMissionStatus(assignment.missionStatus);
    setAdjustoId(assignment.adjustoId || "");
  }, [assignment]);

  const notifyParent = React.useCallback(
    (
      newActivities: Activity[],
      newStatus: AssignmentStatus,
      validate: boolean = false,
    ): boolean => {
      if (validate) {
        if (!rules) return false;
        const result = validateActivitiesList(newActivities, rules);
        if (!result.valid && result.message) {
          showAlert(result.message, "warning");
          return false;
        }
      }
      onChange({
        ...assignment,
        assignmentStatus: newStatus,
        activities: newActivities,
      });
      return true;
    },
    [assignment, rules, onChange, showAlert],
  );

  const handleActivityChange = React.useCallback(
    (index: number, field: string, value: string) => {
      if (!rules || !activitiesChanged) return;

      const newActivities = [...localActivities];
      const target = { ...newActivities[index] };

      if (field === "activityTypeCode") {
        target.activityType = { code: value as ActivityTypeCode };
        const selectedRule = rules.rules.find((rule) => rule.activityTypeCode === value) as
          | Rule
          | undefined;
        const firstPossibleReturn = selectedRule?.possibleReturns?.[0];
        target.returnStatus = (firstPossibleReturn?.returnStatus as ActivityReturnStatus) || "";

        if (!target.responseDetail) {
          target.responseDetail = { freeField: "", stepCode: "", dynamicProperties: [] };
        }
        target.responseDetail.stepCode = firstPossibleReturn?.stepCode || "";

        const hasReasons = selectedRule?.possibleReturns.some(
          (r) => r.reasons && r.reasons.length > 0,
        );
        if (!hasReasons) {
          delete target.stepFailureReason;
        }
      } else if (field === "returnStatus") {
        target.returnStatus = value as ActivityReturnStatus;
        if (!target.responseDetail) {
          target.responseDetail = { freeField: "", stepCode: "", dynamicProperties: [] };
        }

        const currentRule = rules.rules.find(
          (rule) => rule.activityTypeCode === target.activityType?.code,
        );
        const selectedStatus = currentRule?.possibleReturns.find((r) => r.returnStatus === value);

        if (!selectedStatus?.reasons || selectedStatus.reasons.length === 0) {
          delete target.stepFailureReason;
        }
      } else if (field === "stepCode") {
        if (!target.responseDetail) {
          target.responseDetail = { freeField: "", stepCode: "", dynamicProperties: [] };
        }
        target.responseDetail.stepCode = value;

        const currentRule = rules.rules.find(
          (rule) => rule.activityTypeCode === target.activityType?.code,
        );
        const selectedPossibleReturn = currentRule?.possibleReturns.find(
          (r) => r.stepCode === value,
        );

        if (selectedPossibleReturn) {
          target.returnStatus = selectedPossibleReturn.returnStatus as ActivityReturnStatus;

          if (!selectedPossibleReturn.reasons || selectedPossibleReturn.reasons.length === 0) {
            delete target.stepFailureReason;
          }
        }
      } else if (field === "stepFailureReasonCode") {
        const currentRule = rules.rules.find(
          (rule) => rule.activityTypeCode === target.activityType?.code,
        );
        const possibleReturn = currentRule?.possibleReturns?.find(
          (r) => r.stepCode === target.responseDetail?.stepCode,
        );

        if (!value || !possibleReturn?.reasons?.length) {
          delete target.stepFailureReason;
        } else {
          const selectedReason = possibleReturn.reasons.find(
            (reason) => reason.failureReasonCode === value,
          );
          if (selectedReason) {
            target.stepFailureReason = {
              code: selectedReason.failureReasonCode as FailureReasonCode,
              label: selectedReason.failureReasonLabel,
            };
          }
        }
      } else if (field === "issueDate") {
        target.issueDate = value;
      }

      newActivities[index] = target;
      setLocalActivities(newActivities);
      notifyParent(newActivities, localStatus, false);
    },
    [activitiesChanged, localActivities, localStatus, notifyParent, rules],
  );

  const handleAddActivity = (): void => {
    if (!rules || !notifyParent(localActivities, localStatus, true)) return;

    const nextRule = getNextActivityRule(rules, localActivities, logisticsModeCode);
    if (!nextRule) {
      showAlert("Aucune activité disponible pour cette étape.", "warning");
      return;
    }

    const pr = nextRule.possibleReturns[0];
    const returnStatus = (pr?.returnStatus || "") as ActivityReturnStatus;
    const stepCode = pr?.stepCode || "";
    const newAct: Activity = {
      issueDate: new Date().toISOString(),
      activityType: {
        code: nextRule.activityTypeCode as ActivityTypeCode,
      },
      returnStatus,
      responseDetail: {
        freeField: "",
        stepCode,
        dynamicProperties: [],
      },
    };
    const updated = [...localActivities, newAct];
    setLocalActivities(updated);
    notifyParent(updated, localStatus, false);
  };

  const suggestedValuesByIndex = React.useMemo(
    () => (rules ? getSuggestedValuesByIndex(assignment, localActivities, rules) : {}),
    [assignment, localActivities, rules],
  );

  const handleSpecificFieldChange = React.useCallback(
    (index: number, fieldCode: ResponseDetailsPropertiesKeyCode | "freeField", value: string) => {
      if (!activitiesChanged) return;
      if (!rules) return;
      const updatedActivities = [...localActivities];
      const activity = updatedActivities[index];
      if (!activity.responseDetail) {
        const rule = rules.rules.find((r) => r.activityTypeCode === activity.activityType?.code);
        const matches =
          rule?.possibleReturns.filter((pr) => pr.returnStatus === activity.returnStatus) ?? [];
        const inferredStepCode = matches.length === 1 ? matches[0].stepCode : "";
        activity.responseDetail = {
          freeField: "",
          stepCode: inferredStepCode,
          dynamicProperties: [],
        };
      }
      if (fieldCode === "freeField") {
        activity.responseDetail.freeField = value;
      } else {
        const dynamicProperties = activity.responseDetail.dynamicProperties || [];
        const existingPropertyIndex = dynamicProperties.findIndex(
          (prop: DynamicProperty) => prop.key === fieldCode,
        );
        if (existingPropertyIndex >= 0) {
          dynamicProperties[existingPropertyIndex].value = value;
        } else {
          dynamicProperties.push({
            key: fieldCode,
            value: value,
          } as DynamicProperty);
        }
        activity.responseDetail.dynamicProperties = dynamicProperties;
      }
      setLocalActivities(updatedActivities);
      notifyParent(updatedActivities, localStatus, false);
    },
    [activitiesChanged, localActivities, localStatus, notifyParent],
  );

  const logisticsModeCode = assignment.servicePoint?.logisticsMode?.code || "DEFAULT";
  const nextIsExternal = isNextActivityExternal(rules, localActivities, logisticsModeCode);

  const handleAdjustoIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAdjustoId = event.target.value;
    setAdjustoId(newAdjustoId);
    onChange({
      ...assignment,
      adjustoId: newAdjustoId,
      assignmentStatus: localStatus,
      activities: localActivities,
    });
  };

  const handleSaveAdjustoId = async () => {
    if (!adjustoId.trim()) {
      showAlert("L'identifiant Adjusto ne peut pas être vide", "warning");
      return;
    }

    confirm({
      title: "Mettre à jour l'identifiant Adjusto",
      message: "Êtes-vous sûr de vouloir mettre à jour l'identifiant Adjusto ?",
      onConfirm: async () => {
        setSavingAdjustoId(true);

        const updateData = {
          request: {
            adjustoId: adjustoId.trim(),
          },
        };

        await updateAdjustoId(assignment.externalAssignmentId, updateData);
        setSavingAdjustoId(false);
      },
    });
  };

  const handleMissionStatusChange = (newMissionStatus: MissionStatus) => {
    confirm({
      title: "Mettre à jour le statut de prestation",
      message: "Êtes-vous sûr de vouloir changer le statut de prestation ?",
      onConfirm: async () => {
        setSavingMissionStatus(true);

        const updateData = {
          request: {
            missionStatus: newMissionStatus,
          },
        };

        const result = await updateMissionStatus(assignment.externalAssignmentId, updateData);

        if (result && result.return.status === "OK") {
          setMissionStatus(newMissionStatus);
          onChange({
            ...assignment,
            missionStatus: newMissionStatus,
          });
        }

        setSavingMissionStatus(false);
      },
    });
  };

  if (!rules) {
    return null;
  }

  return (
    <Box sx={{ px: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: 3,
          mb: 3,
        }}
      >
        <Box sx={{ width: "50%" }}>
          <Typography level="body-sm" fontWeight="bold" mb={0.5}>
            Identifiant Adjusto :
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Input
              value={adjustoId}
              onChange={handleAdjustoIdChange}
              placeholder="Saisir l'identifiant Adjusto"
              size="sm"
              sx={{ flexGrow: 1 }}
            />
            <Tooltip title="Sauvegarder l'identifiant Adjusto" placement="top">
              <IconButton
                size="sm"
                variant="solid"
                color="primary"
                disabled={savingAdjustoId || !adjustoId.trim()}
                onClick={handleSaveAdjustoId}
              >
                <SaveIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ width: "50%" }}>
          <Typography level="body-sm" fontWeight="bold" mb={0.5}>
            Statut de prestation :
          </Typography>
          <MissionStatusSelector
            value={missionStatus}
            onChange={handleMissionStatusChange}
            disabled={savingMissionStatus}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          mt: 2,
        }}
      >
        <Typography level="body-sm" fontWeight="bold" mb={1.5}>
          Activités :
        </Typography>
        {localActivities.map((activity, idx) => {
          const rule = rules.rules.find((r) => r.activityTypeCode === activity.activityType?.code);
          const returnStatuses = rule?.possibleReturns ?? [];
          const reasons =
            returnStatuses.find((s) => s.stepCode === activity.responseDetail?.stepCode)?.reasons ||
            [];
          return (
            <ActivityEditor
              key={idx}
              index={idx}
              activity={activity}
              activities={localActivities}
              onChange={handleActivityChange}
              rules={rules}
              returnStatuses={returnStatuses}
              reasons={reasons}
              onChangeSpecificField={
                handleSpecificFieldChange as (
                  index: number,
                  fieldCode: string,
                  value: string,
                ) => void
              }
              suggestedValues={suggestedValuesByIndex[idx] ?? {}}
              isLastActivity={idx === localActivities.length - 1}
              logisticsMode={assignment.servicePoint?.logisticsMode?.code || "DEFAULT"}
              activitiesChanged={activitiesChanged}
            />
          );
        })}

        {!activitiesChanged && (
          <Box mt={2}>
            <Button
              startDecorator={<AddIcon />}
              onClick={handleAddActivity}
              disabled={nextIsExternal}
              sx={{
                backgroundColor: nextIsExternal ? "neutral.softBg" : "neutral.softBg",
                color: nextIsExternal ? "text.disabled" : "text.primary",
                "&:hover": {
                  backgroundColor: nextIsExternal ? "neutral.softBg" : "neutral.200",
                },
              }}
            >
              Ajouter une activité
            </Button>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <CommonReadOnlyLayout assignment={assignment} />
      </Box>
    </Box>
  );
};

export default AssignmentEditorFields;
